import React from 'react';
import WizardStepper from './WizardStepper';
import { useWorkoutWizard } from '../hooks/useWorkoutWizard';
import { WIZARD_STEPS } from '../constants/workoutWizardSteps';
import WizardStepProfile from './wizard/WizardStepProfile';
import WizardStepPhysical from './wizard/WizardStepPhysical';
import WizardStepGoals from './wizard/WizardStepGoals';
import WizardStepLimitations from './wizard/WizardStepLimitations';
import WizardStepPipeline from './wizard/WizardStepPipeline';
import WizardStepReview from './wizard/WizardStepReview';
import { useOnboardingPack } from '../context/OnboardingPackProvider';

export default function WorkoutWizardModal() {
  const { wizardOpen, wizardMode } = useOnboardingPack();
  const wizard = useWorkoutWizard();

  if (!wizardOpen) return null;

  const {
    currentStep,
    formData,
    setFormData,
    stepError,
    pipelineLoading,
    pipelinePhase,
    pipelineErr,
    engineLabel,
    suggestions,
    perWorkoutNotes,
    globalNotes,
    setGlobalNotes,
    extraDetail,
    setExtraDetail,
    workoutComposition,
    setWorkoutComposition,
    draftSavedAt,
    applyLoading,
    regenerationRound,
    canRegenerate,
    goNext,
    goBack,
    handleClose,
    cancelPipeline,
    handleRegenerate,
    handleApplyAll,
    removeSuggestion,
    updatePerWorkoutNote,
    clearDraft,
    jobId,
    applyOne,
    stepSaving,
  } = wizard;

  const showBack =
    currentStep !== WIZARD_STEPS.PROFILE &&
    currentStep !== WIZARD_STEPS.PIPELINE;
  const showNext =
    currentStep !== WIZARD_STEPS.PIPELINE && currentStep !== WIZARD_STEPS.REVIEW;
  const isPipeline = currentStep === WIZARD_STEPS.PIPELINE;
  const isReview = currentStep === WIZARD_STEPS.REVIEW;

  const title =
    wizardMode === 'forced' ? 'Configurar treinos com IA' : 'Criar divisão com IA';

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        className="flex max-h-[94vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-iron-accent/40 bg-iron-bg shadow-[0_20px_80px_rgba(0,0,0,0.55)] sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workout-wizard-title"
      >
        <div className="shrink-0 border-b border-iron-border px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 id="workout-wizard-title" className="font-display text-xl tracking-wide text-iron-accent sm:text-2xl">
                {title}
              </h2>
              <p className="mt-1 font-mono text-[10px] text-iron-muted">
                Perfil → geração → revisão
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 text-iron-muted hover:text-iron-text"
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
          <WizardStepper currentStep={currentStep} className="mt-3" />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {currentStep === WIZARD_STEPS.PROFILE && (
            <WizardStepProfile formData={formData} onChange={setFormData} />
          )}
          {currentStep === WIZARD_STEPS.PHYSICAL && (
            <WizardStepPhysical formData={formData} onChange={setFormData} />
          )}
          {currentStep === WIZARD_STEPS.GOALS && (
            <WizardStepGoals formData={formData} onChange={setFormData} />
          )}
          {currentStep === WIZARD_STEPS.LIMITATIONS && (
            <WizardStepLimitations
              formData={formData}
              onChange={setFormData}
              globalNotes={globalNotes}
              onGlobalNotesChange={setGlobalNotes}
              extraDetail={extraDetail}
              onExtraDetailChange={setExtraDetail}
              workoutComposition={workoutComposition}
              onWorkoutCompositionChange={setWorkoutComposition}
            />
          )}
          {isPipeline && (
            <WizardStepPipeline
              error={pipelineErr}
              jobId={jobId}
              onCancel={cancelPipeline}
            />
          )}
          {isReview && (
            <WizardStepReview
              suggestions={suggestions}
              perWorkoutNotes={perWorkoutNotes}
              engineLabel={engineLabel}
              globalNotes={globalNotes}
              onGlobalNotesChange={setGlobalNotes}
              extraDetail={extraDetail}
              onExtraDetailChange={setExtraDetail}
              workoutComposition={workoutComposition}
              onWorkoutCompositionChange={setWorkoutComposition}
              draftSavedAt={draftSavedAt}
              onPerWorkoutNoteChange={updatePerWorkoutNote}
              onRemoveSuggestion={removeSuggestion}
              onClearDraft={clearDraft}
              onApplyOne={applyOne}
              onApplyAll={handleApplyAll}
              onRegenerate={handleRegenerate}
              applyLoading={applyLoading}
              canRegenerate={canRegenerate}
              regenerationRound={regenerationRound}
            />
          )}

          {stepError && !isPipeline && (
            <p className="mt-3 font-mono text-xs text-red-400">{stepError}</p>
          )}
        </div>

        {!isPipeline && !isReview && (
          <div className="shrink-0 flex gap-2 border-t border-iron-border px-5 py-4">
            {showBack && (
              <button type="button" className="btn-secondary flex-1" onClick={goBack}>
                Voltar
              </button>
            )}
            {showNext && (
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={goNext}
                disabled={stepSaving || pipelineLoading}
              >
                {stepSaving
                  ? 'Salvando…'
                  : currentStep === WIZARD_STEPS.LIMITATIONS
                    ? 'Gerar treinos'
                    : 'Próximo'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
